import { Center, Grid, Group, SimpleGrid, Space, Stack, Table, Text, Title } from '@mantine/core'
import { GetStationDetailsForSpeciResponse, SpeciDto } from '@loom-api-types'
import classes from './speci-woolstore-form.module.css'
import { consignmentStatusValueToString } from '@shared/constants/speci-consignment-status'

export interface SpeciWoolstoreFormProps {
  stationRecordNumber: string
  speci: SpeciDto
  stationDetailsForSpeci: GetStationDetailsForSpeciResponse
  sentByText: string
  totalBales: number
}

export const SpeciWoolstoreForm = ({
  stationRecordNumber,
  speci,
  stationDetailsForSpeci,
  totalBales,
  sentByText,
}: SpeciWoolstoreFormProps) => {
  // Replace new lines with spaces in the classer comment to reduce comment height
  const classerComment = speci.classerComment ? speci.classerComment.replace(/\n/g, ' ') : undefined

  return (
    <Table className={classes.layoutTable} withRowBorders={false}>
      {/* Header */}
      <Table.Thead>
        <Table.Tr>
          <Table.Td>
            <Stack gap='sm' mb='sm'>
              <Group justify='space-between' align='flex-end'>
                <Title size='h2'>Zentera ZQ Specification</Title>
                <Text>
                  <Text fw={700} span>
                    Woolstore:{' '}
                  </Text>
                  {speci?.woolStore}
                </Text>
              </Group>
              <Group justify='space-between' gap='md'>
                <Text>
                  <Text fw={700} span>
                    Brand:{' '}
                  </Text>
                  {stationDetailsForSpeci.brand}
                </Text>
                <Text>
                  <Text fw={700} span>
                    Station ID:{' '}
                  </Text>
                  {stationRecordNumber}
                </Text>
                <Text>
                  <Text fw={700} span>
                    Breed(s):{' '}
                  </Text>
                  {speci?.breeds.join(', ')}
                </Text>
                <Text>
                  <Text fw={700} span>
                    Total Bales:{' '}
                  </Text>
                  {totalBales}
                </Text>
              </Group>
            </Stack>
          </Table.Td>
        </Table.Tr>
      </Table.Thead>
      {/* Lines table */}
      <Table.Tbody>
        <Table.Tr>
          <Table.Td>
            <Table className={classes.speciLines} withRowBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th className={classes.lineNoColumn}>
                    Line
                    <br />
                    No
                  </Table.Th>
                  <Table.Th className={classes.lineDescriptionColumn}>
                    Line
                    <br />
                    Description
                  </Table.Th>
                  <Table.Th className={classes.codeColumn}>Code</Table.Th>
                  <Table.Th className={classes.refNoColumn}>Ref No</Table.Th>
                  <Table.Th className={classes.baleNumbersColumn}>Bale Numbers</Table.Th>
                  <Table.Th className={classes.baleCountColumn}>Total</Table.Th>
                  <Table.Th className={classes.traditionalDescriptionColumn}>
                    Traditional
                    <br />
                    Description
                  </Table.Th>
                  <Table.Th className={classes.instructionColumn}>Instruction</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {speci.lines.map((line, lineIndex) => (
                  <Table.Tr key={`line-${lineIndex}`}>
                    <Table.Td>{line.number}</Table.Td>
                    <Table.Td>{line.description}</Table.Td>
                    <Table.Td>
                      <Space />
                    </Table.Td>
                    <Table.Td>
                      <Space />
                    </Table.Td>
                    <Table.Td>
                      <SimpleGrid cols={10} spacing={0} verticalSpacing='xs'>
                        {line.baleNumbers.map((value: number, index: number) => (
                          <Center key={`bale-pill-${value}-${index}-line-${lineIndex}`}>
                            <Text
                              className={classes.baleNumber}
                              size={value.toString().length > 4 ? 'xs' : 'md'}
                            >
                              {value}
                            </Text>
                          </Center>
                        ))}
                      </SimpleGrid>
                    </Table.Td>
                    <Table.Td className={classes.alignRight}>{line.baleNumbers.length}</Table.Td>
                    <Table.Td>{line.traditionalDescription}</Table.Td>
                    <Table.Td>{line.instructions}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.Td>
        </Table.Tr>
      </Table.Tbody>
      {/* Footer */}
      <Table.Tfoot>
        <Table.Tr>
          <Table.Td>
            <Grid mt='sm'>
              <Grid.Col span={8}>
                <Stack gap={0}>
                  <Text fw={700}>Comments/Instructions:</Text>
                  {classerComment ? (
                    <Text size='sm'> {classerComment}</Text>
                  ) : (
                    <Text size='sm' c='dimmed'>
                      No comment
                    </Text>
                  )}
                </Stack>
              </Grid.Col>
              <Grid.Col span={4}>
                <Stack gap='xs'>
                  {speci.consignmentStatus && (
                    <Stack gap={0}>
                      <Text fw={700} span>
                        Consignment status:
                      </Text>
                      <Text>{consignmentStatusValueToString(speci.consignmentStatus)}</Text>
                    </Stack>
                  )}
                  {sentByText && (
                    <Stack gap={0}>
                      <Text fw={700} span>
                        Sent by:
                      </Text>
                      <Text>{sentByText}</Text>
                    </Stack>
                  )}
                </Stack>
              </Grid.Col>
            </Grid>
          </Table.Td>
        </Table.Tr>
      </Table.Tfoot>
    </Table>
  )
}
